import { LVGLSchemaService, parseXmlWithDomParser } from '@lved/shared-core';
import type { BasicPlatformApi } from '@lved/shared-core/types';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { Emitter, Event } from '@theia/core/lib/common';
import { injectable } from '@theia/core/shared/inversify';

@injectable()
export class XmlSchemaContribution implements FrontendApplicationContribution {
    protected schemaService: LVGLSchemaService | undefined;
    protected schemaServiceInitPromise: Promise<LVGLSchemaService | undefined> | undefined;
    protected readonly onDidChangeSchemaServiceEmitter: Emitter<LVGLSchemaService> = new Emitter<LVGLSchemaService>();
    readonly onDidChangeSchemaService: Event<LVGLSchemaService> = this.onDidChangeSchemaServiceEmitter.event;

    /**
     * Schema is created by `TheiaPlatformApiContribution` after `installPlatformApi` (one-way dependency).
     * Other contributions should call `ensureInitialized()` after platform startup.
     */
    async onStart(): Promise<void> {
        return undefined;
    }

    onStop(): void {
        this.onDidChangeSchemaServiceEmitter.dispose();
    }

    getSchemaService(): LVGLSchemaService | undefined {
        return this.schemaService;
    }

    async ensureInitialized(): Promise<LVGLSchemaService | undefined> {
        if (this.schemaService) return this.schemaService;
        if (this.schemaServiceInitPromise) return this.schemaServiceInitPromise;
        return undefined;
    }

    /**
     * Called only from `TheiaPlatformApiContribution` once the platform API exists.
     */
    async initializeFromPlatformApi(
        platformApi: BasicPlatformApi,
        projectPath: string
    ): Promise<LVGLSchemaService | undefined> {
        if (this.schemaService) return this.schemaService;
        if (this.schemaServiceInitPromise) return this.schemaServiceInitPromise;

        this.schemaServiceInitPromise = this.buildSchemaService(platformApi, projectPath);

        try {
            const schemaService: LVGLSchemaService | undefined = await this.schemaServiceInitPromise;
            if (schemaService) {
                this.schemaService = schemaService;
                this.onDidChangeSchemaServiceEmitter.fire(schemaService);
            }
            return this.schemaService;
        } finally {
            this.schemaServiceInitPromise = undefined;
        }
    }

    async reloadForProject(projectPath: string): Promise<LVGLSchemaService | undefined> {
        if (!this.schemaService) return undefined;
        await this.schemaService.loadEverythingInParallel(projectPath);
        this.onDidChangeSchemaServiceEmitter.fire(this.schemaService);
        return this.schemaService;
    }

    protected async buildSchemaService(
        platformApi: BasicPlatformApi,
        projectPath: string
    ): Promise<LVGLSchemaService | undefined> {
        const schemaService: LVGLSchemaService | undefined = LVGLSchemaService.getInstance(
            platformApi,
            projectPath,
            parseXmlWithDomParser
        );

        if (!schemaService) return undefined;

        await schemaService.loadEverythingInParallel(projectPath);
        window.__lvedInitialSchemaService = schemaService;
        window.schemaServiceStore?.getState().setService(schemaService);

        return schemaService;
    }
}
